/**
 * Stage 1 — The forced-choice gauntlet.
 *
 * One question at a time. The user picks an option (revealed-preference
 * signal) and types one sentence on why (low-trust narration that doubles
 * as retrieval-indexing fodder downstream).
 *
 * Anti-fatigue mechanics:
 *   - Single progress bar across the top
 *   - Keyboard nav (1-4 picks an option, Enter/→ advances)
 *   - Auto-save to draft after every answer (resume-friendly)
 *   - Mid-gauntlet milestone pulse at 1/3 and 2/3 of the way through
 *
 * No "right answer" framing — the copy under each question reminds the
 * user that all options are defensible, which produces less hedged answers.
 */

import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { OnboardingDraftStage1 } from '@arena/shared';
import { questionById, type Question } from '../../data/question-bank';

interface Stage1Props {
  questionIds: string[];
  answers: OnboardingDraftStage1['answers'];
  /** Called after every answer change so the container can persist the draft. */
  onAnswer: (
    questionId: string,
    next: { choice: string; why: string; hardness?: 1 | 2 | 3 },
  ) => void;
  /** Called when the user finishes the last question. */
  onComplete: () => void;
  /** Called when the user wants to bail out (back to library). */
  onExit: () => void;
}

const MILESTONES = [1 / 3, 2 / 3];

export function Stage1ForcedChoice({
  questionIds,
  answers,
  onAnswer,
  onComplete,
  onExit,
}: Stage1Props) {
  // Find the first unanswered question to resume on (or 0 if fresh).
  const initialIndex = (() => {
    for (let i = 0; i < questionIds.length; i++) {
      if (!answers[questionIds[i]!]?.choice) return i;
    }
    return Math.max(0, questionIds.length - 1);
  })();
  const [index, setIndex] = useState(initialIndex);

  const currentId = questionIds[index];
  const question: Question | undefined = currentId
    ? questionById(currentId)
    : undefined;
  const currentAnswer = currentId ? answers[currentId] : undefined;
  const [choice, setChoice] = useState<string>(currentAnswer?.choice ?? '');
  const [why, setWhy] = useState<string>(currentAnswer?.why ?? '');
  const [hardness, setHardness] = useState<1 | 2 | 3 | undefined>(
    currentAnswer?.hardness,
  );
  const [milestoneShown, setMilestoneShown] = useState<number | null>(null);

  const whyRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLElement>(null);

  // Reset local state when we move to a different question. Intentionally
  // does NOT depend on `answers` — if it did, every keystroke would round-
  // trip through the parent's answers map (which trims) and stomp the
  // local input, making it impossible to type a trailing space.
  useEffect(() => {
    const next = currentId ? answers[currentId] : undefined;
    setChoice(next?.choice ?? '');
    setWhy(next?.why ?? '');
    setHardness(next?.hardness);
    // Scroll the card into view on advance so long whys don't strand the user.
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // Show a milestone pulse at 33% and 66% completion (one-shot, not blocking).
  useEffect(() => {
    const ratio = (index + 1) / questionIds.length;
    const hit = MILESTONES.findIndex((m) => ratio >= m && ratio < m + 1 / questionIds.length);
    if (hit !== -1 && milestoneShown !== hit) {
      setMilestoneShown(hit);
      const t = window.setTimeout(() => setMilestoneShown(null), 2200);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [index, questionIds.length, milestoneShown]);

  if (!question || !currentId) {
    return (
      <div className="onboarding-error" role="alert">
        Could not load this question. Try restarting the analyzer.
      </div>
    );
  }

  const persistCurrent = (
    nextChoice: string,
    nextWhy: string,
    nextHardness: 1 | 2 | 3 | undefined,
  ) => {
    if (!nextChoice) return; // nothing meaningful to persist without a pick
    onAnswer(currentId, {
      choice: nextChoice,
      why: nextWhy.trim(),
      ...(nextHardness ? { hardness: nextHardness } : {}),
    });
  };

  const handlePickOption = (opt: string) => {
    const next = choice === opt ? '' : opt;
    setChoice(next);
    if (next) {
      persistCurrent(next, why, hardness);
      // Move focus to the why field once they've committed to an option.
      window.setTimeout(() => whyRef.current?.focus(), 80);
    }
  };

  const handleWhyChange = (value: string) => {
    setWhy(value);
    if (choice) persistCurrent(choice, value, hardness);
  };

  const handleHardness = (level: 1 | 2 | 3) => {
    const next = hardness === level ? undefined : level;
    setHardness(next);
    if (choice) persistCurrent(choice, why, next);
  };

  const canAdvance = !!choice;
  const isLast = index >= questionIds.length - 1;

  const advance = () => {
    if (!canAdvance) return;
    if (isLast) {
      onComplete();
      return;
    }
    setIndex((i) => i + 1);
  };

  const back = () => {
    if (index === 0) return;
    setIndex((i) => i - 1);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    advance();
  };

  // Number-key shortcuts (1–4 pick an option) — only when focus is not in an input.
  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement
    ) {
      return;
    }
    if (['1', '2', '3', '4'].includes(e.key)) {
      const idx = parseInt(e.key, 10) - 1;
      if (idx < question.options.length) {
        handlePickOption(question.options[idx]!);
        e.preventDefault();
      }
    }
  };

  const answeredCount = Object.values(answers).filter((a) => a.choice).length;
  const progressPct = Math.round(((index + 1) / questionIds.length) * 100);
  const milestoneCopy =
    milestoneShown === 0
      ? 'A third in. Stay with it — the next batch gets at how you reason, not what you conclude.'
      : milestoneShown === 1
        ? 'Two-thirds done. The test debate at the end is where this gets interesting.'
        : null;

  return (
    <div
      className="onboarding-stage1"
      onKeyDown={handleKey}
      tabIndex={-1}
    >
      <div className="onboarding-progress" aria-hidden="true">
        <div
          className="onboarding-progress__fill"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div className="onboarding-progress__meta">
        <span className="onboarding-progress__count">
          {index + 1} of {questionIds.length}
        </span>
        <span className="onboarding-progress__domain">{question.domain}</span>
        <button
          type="button"
          className="onboarding-progress__exit"
          onClick={onExit}
        >
          Save &amp; exit
        </button>
      </div>

      {milestoneCopy && (
        <p className="onboarding-stage1__milestone" role="status">
          {milestoneCopy}
        </p>
      )}

      <article ref={cardRef} className="onboarding-question-card">
        <p className="onboarding-question-card__hint">
          No right answer. Pick the one closest to you.
        </p>
        <h2 className="onboarding-question-card__scenario">
          {question.scenario}
        </h2>

        <div
          className="onboarding-question-card__options"
          role="radiogroup"
          aria-label="Pick the option closest to you"
        >
          {question.options.map((opt, i) => {
            const selected = choice === opt;
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={selected}
                className={
                  'onboarding-option' +
                  (selected ? ' onboarding-option--selected' : '')
                }
                onClick={() => handlePickOption(opt)}
              >
                <span className="onboarding-option__num">{i + 1}</span>
                <span className="onboarding-option__text">{opt}</span>
              </button>
            );
          })}
        </div>

        <form className="onboarding-question-card__why" onSubmit={handleSubmit}>
          <label
            className="onboarding-question-card__why-label"
            htmlFor={`why-${currentId}`}
          >
            One sentence on why — the texture matters more than the polish.
          </label>
          <textarea
            ref={whyRef}
            id={`why-${currentId}`}
            className="onboarding-question-card__why-input"
            value={why}
            onChange={(e) => handleWhyChange(e.target.value)}
            placeholder="Because…"
            rows={2}
            maxLength={500}
          />

          <div className="onboarding-question-card__hardness">
            <span className="onboarding-question-card__hardness-label">
              How hard was this one?
            </span>
            {([1, 2, 3] as const).map((level) => (
              <button
                key={level}
                type="button"
                className={
                  'onboarding-hardness' +
                  (hardness === level ? ' onboarding-hardness--on' : '')
                }
                onClick={() => handleHardness(level)}
                aria-pressed={hardness === level}
                aria-label={
                  level === 1 ? 'Easy' : level === 2 ? 'Some thought' : 'Real thought'
                }
              >
                {level === 1 ? '◦' : level === 2 ? '◯' : '●'}
              </button>
            ))}
          </div>

          <div className="onboarding-question-card__actions">
            <button
              type="button"
              className="onboarding-question-card__back"
              onClick={back}
              disabled={index === 0}
            >
              ← Back
            </button>
            <div className="onboarding-question-card__answered">
              {answeredCount} answered
            </div>
            <button
              type="submit"
              className="onboarding-question-card__advance"
              disabled={!canAdvance}
            >
              {isLast ? 'See your brain' : 'Next'}{' '}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </form>
      </article>
    </div>
  );
}
